import React from 'react';
import { Box, Button, Select } from 'grommet';
import PropTypes from 'prop-types';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { BlockId } from './BlockIdExtension';
import {
    EntryHeader,
    EntryHeaderName,
    EntryHeaderDate,
    EntryDateParagraph,
} from './EntryHeaderExtension';
import { RESUME_EDITOR_STYLES } from './resumeDocumentStyles';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Paint as HighlightIcon,
    UnorderedList,
    OrderedList,
    TextAlignLeft,
    TextAlignCenter,
    TextAlignRight,
    Table,
} from 'grommet-icons';

const HEADING_OPTIONS = [
    { label: 'Normal text', level: 0 },
    { label: 'Heading 1', level: 1 },
    { label: 'Heading 2', level: 2 },
    { label: 'Heading 3', level: 3 },
];

const activeHeadingLabel = (editor) => {
    const active = HEADING_OPTIONS.find(
        (option) =>
            option.level > 0 &&
            editor.isActive('heading', { level: option.level }),
    );
    return active ? active.label : HEADING_OPTIONS[0].label;
};

const ToolbarButton = ({ active, disabled = false, icon, onClick }) => (
    <Button
        active={active}
        disabled={disabled}
        icon={icon}
        onClick={onClick}
        plain
        pad={{ horizontal: 'xsmall', vertical: '6px' }}
        round="4px"
        background={active ? 'light-3' : 'transparent'}
    />
);

const EditorToolbar = ({ editor }) => {
    if (!editor) return null;

    return (
        <Box
            direction="row"
            gap="xsmall"
            wrap
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
            border={{
                color: 'enrich-green-light',
                size: '3px',
                side: 'bottom',
            }}>
            <Box width="150px">
                <Select
                    options={HEADING_OPTIONS.map((option) => option.label)}
                    value={activeHeadingLabel(editor)}
                    onChange={({ option }) => {
                        const selected = HEADING_OPTIONS.find(
                            (heading) => heading.label === option,
                        );
                        const chain = editor.chain().focus();
                        if (!selected || selected.level === 0) {
                            chain.setParagraph().run();
                        } else {
                            chain.setHeading({ level: selected.level }).run();
                        }
                    }}
                />
            </Box>
            <ToolbarButton
                active={editor.isActive('bold')}
                icon={<Bold />}
                onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ToolbarButton
                active={editor.isActive('italic')}
                icon={<Italic />}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <ToolbarButton
                active={editor.isActive('underline')}
                icon={<UnderlineIcon />}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <ToolbarButton
                active={editor.isActive('highlight')}
                icon={<HighlightIcon />}
                onClick={() => editor.chain().focus().toggleHighlight().run()}
            />
            <ToolbarButton
                active={editor.isActive('bulletList')}
                icon={<UnorderedList />}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <ToolbarButton
                active={editor.isActive('orderedList')}
                icon={<OrderedList />}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
            <ToolbarButton
                active={editor.isActive({ textAlign: 'left' })}
                icon={<TextAlignLeft />}
                onClick={() =>
                    editor.chain().focus().setTextAlign('left').run()
                }
            />
            <ToolbarButton
                active={editor.isActive({ textAlign: 'center' })}
                icon={<TextAlignCenter />}
                onClick={() =>
                    editor.chain().focus().setTextAlign('center').run()
                }
            />
            <ToolbarButton
                active={editor.isActive({ textAlign: 'right' })}
                icon={<TextAlignRight />}
                onClick={() =>
                    editor.chain().focus().setTextAlign('right').run()
                }
            />
            <ToolbarButton
                active={editor.isActive('entryHeader')}
                disabled={editor.isActive('listItem')}
                icon={<Table />}
                onClick={() =>
                    editor.chain().focus().insertEntryHeader().run()
                }
            />
        </Box>
    );
};

const activeBlockId = (editor) => {
    const { $from } = editor.state.selection;
    for (let depth = $from.depth; depth >= 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.attrs?.blockId) return node.attrs.blockId;
    }
    return null;
};

export const ResumeEditor = ({
    content,
    onChange,
    onActiveBlockChange,
    onReady,
    maxHeight,
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Highlight,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            BlockId,
            EntryHeader,
            EntryHeaderName,
            EntryHeaderDate,
            EntryDateParagraph,
        ],
        content: content || '',
        editable: true,
        // Fires after BlockId has stamped any missing ids, so callers get HTML
        // whose data-block-id attributes match what comments will anchor to.
        // Relying on the stamping transaction to reach them via onUpdate is a
        // Tiptap implementation detail, not a contract.
        onCreate: ({ editor }) => {
            onReady && onReady(editor.getHTML());
        },
        onUpdate: ({ editor }) => {
            onChange && onChange(editor.getHTML());
        },
        onSelectionUpdate: ({ editor }) => {
            onActiveBlockChange && onActiveBlockChange(activeBlockId(editor));
        },
    });

    return (
        <Box
            background="white"
            round="6px"
            border={{ color: 'enrich-green-light', size: '3px' }}
            width="100%"
            overflow="hidden">
            <style>{RESUME_EDITOR_STYLES}</style>
            <Box flex={false}>
                <EditorToolbar editor={editor} />
            </Box>
            <Box pad="small" overflow="auto" height={{ max: maxHeight }}>
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
};

ToolbarButton.propTypes = {
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    icon: PropTypes.node,
    onClick: PropTypes.func,
};

EditorToolbar.propTypes = {
    editor: PropTypes.object,
};

ResumeEditor.propTypes = {
    content: PropTypes.string,
    onChange: PropTypes.func,
    onActiveBlockChange: PropTypes.func,
    onReady: PropTypes.func,
    maxHeight: PropTypes.string,
};

ResumeEditor.defaultProps = {
    maxHeight: '50vh',
};
